import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Grid } from "theme-ui";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { themevals } from "theme/themevals";
import { CHARGE_PRESETS, ChargeKind, addCharge, removeCharge } from "api/Charges";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { CheckMarkIcon, InvoicesIcon } from "components/svg";
import { useToast } from "components/toast";

/**
 * Ad-hoc charge dialogs (AD_HOC_CHARGES_SPEC.md), shared by the project page
 * and the invoice page (2026-11-03). One action writes the client invoice line
 * (client price) and, when ticked, the provider's PO line (provider cost):
 * e.g. trip charge, client $1,000 on the invoice, provider $850 on the PO.
 */

const money = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const gmo = (po: any) => `GMO-${String(po?.sequence ?? "").padStart(5, "0")}`;

/** A real <button> styled as a text link. The typography Span drops onClick, so it cannot be used for actions. */
export const TextLink = ({ children, onClick, style }: { children: React.ReactNode; onClick: () => void; style?: React.CSSProperties }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontFamily: "Poppins", fontSize: "12px", fontWeight: 500, color: themevals.colors.primary, textDecoration: "underline", ...style }}
  >
    {children}
  </button>
);

/**
 * A tickable option with a title and a plain-language consequence underneath.
 * Used for "also put this on the PO" and the fuel-surcharge PO choice: the
 * stock Checkbox shouts its label in capitals and wraps badly in a modal.
 */
export const OptionRow = ({ checked, onChange, title, detail }: { checked: boolean; onChange: (v: boolean) => void; title: React.ReactNode; detail?: React.ReactNode }) => (
  <label
    style={{
      display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "10px 12px",
      border: `1px solid ${checked ? themevals.colors.black : themevals.colors.inputOutline}`, borderRadius: 6, background: "#fff",
      fontFamily: "Poppins",
    }}
  >
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, accentColor: "#000", flex: "0 0 auto" }} />
    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: themevals.colors.black }}>{title}</span>
      {detail && <span style={{ fontSize: 12, color: themevals.colors.dkgrey, lineHeight: "17px" }}>{detail}</span>}
    </span>
  </label>
);

const Figure = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <Flex sx={{ flexDirection: "column", gap: "2px", flex: 1 }}>
    <span style={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>{label}</span>
    <span style={{ fontFamily: "Poppins", fontSize: 15, fontWeight: strong ? 700 : 600, color: themevals.colors.black }}>{value}</span>
  </Flex>
);

type Form = { kind: ChargeKind; name: string; description: string; quantity: string; cost: string; price: string; applyToPO: boolean; purchaseOrderId: string; reason: string };

export const ChargeAddModal = ({ invoice, pos, onClose, onDone }: { invoice: any | null; pos: any[]; onClose: () => void; onDone: () => void }) => {
  const toast = useToast();
  const live = (pos ?? []).filter((po: any) => po && Number(po.status) !== 6);
  const newest = live.reduce((best: any, po: any) => (!best || (po.sequence ?? 0) > (best.sequence ?? 0) ? po : best), null);
  const blank = (): Form => ({ kind: "trip", name: CHARGE_PRESETS[0].name, description: CHARGE_PRESETS[0].description, quantity: "1", cost: "", price: "", applyToPO: !!newest, purchaseOrderId: newest?.id ?? "", reason: "" });
  const [form, setForm] = useState<Form>(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoice) {
      setForm(blank());
      setError(null);
    }
  }, [invoice?.id, newest?.id]);

  if (!invoice) return null;
  const qty = Math.max(1, Math.round(Number(form.quantity) || 1));
  const cost = Number(form.cost) || 0;
  const price = form.price === "" ? cost : Number(form.price) || 0;
  const selectedPO = live.find((po: any) => po.id === form.purchaseOrderId);

  const submit = async () => {
    const c = Number(form.cost);
    const p = form.price === "" ? c : Number(form.price);
    if (!Number.isFinite(c) || c < 0) return setError("Enter what the provider charges us (0 if nothing).");
    if (!Number.isFinite(p) || p < 0) return setError("Enter what the client pays.");
    if (!(c > 0) && !(p > 0)) return setError("A charge needs a provider cost or a client price.");
    if (!form.name.trim()) return setError("Give the charge a name.");
    setBusy(true);
    setError(null);
    const res: any = await addCharge(invoice.id, {
      kind: form.kind,
      name: form.name.trim(),
      description: form.description.trim(),
      quantity: qty,
      cost: c,
      price: p,
      applyToPurchaseOrder: form.applyToPO && !!form.purchaseOrderId,
      purchaseOrderId: form.applyToPO && form.purchaseOrderId ? form.purchaseOrderId : undefined,
      reason: form.reason.trim() || undefined,
    });
    setBusy(false);
    if (res?.error) return setError(res.error);
    toast.add(
      `${form.name.trim()} added: client ${money(p * qty)}${res?.charge?.purchaseOrderSequence ? `, provider ${money(c * qty)} on GMO-${String(res.charge.purchaseOrderSequence).padStart(5, "0")}` : ""}.`,
      "var(--theme-ui-colors-success)",
    );
    if (res?.purchaseOrderSkipped) toast.add(res.purchaseOrderSkipped, "var(--theme-ui-colors-danger)");
    onClose();
    onDone();
  };

  return (
    <Modal isOpen={!!invoice} toggle={onClose} centered size={"sm"} closeOnBackdropClick={false}>
      <ModalHeader toggle={onClose}>
        <ModalIcon>
          <InvoicesIcon fill={"#000"} />
        </ModalIcon>
        <ModalTitleWrapper>
          <ModalTitle>Add charge</ModalTitle>
          <ModalSubtitle>{`On invoice GMI-${String(invoice.sequence ?? "").padStart(5, "0")}`}</ModalSubtitle>
        </ModalTitleWrapper>
      </ModalHeader>
      <ModalBody>
        <SelectInput
          name="chargeKind"
          label="Charge"
          value={form.kind}
          onChange={(val: any) => {
            const preset = CHARGE_PRESETS.find((x) => x.key === val) ?? CHARGE_PRESETS[CHARGE_PRESETS.length - 1];
            setForm((f) => ({ ...f, kind: preset.key, name: preset.name, description: preset.description }));
          }}
          options={CHARGE_PRESETS.map((x) => ({ value: x.key, label: x.name }))}
        />
        {form.kind === "other" && (
          <TextInput name="chargeName" label="Name on the invoice" value={form.name} onChange={(val: any) => setForm((f) => ({ ...f, name: String(val ?? "") }))} />
        )}
        <TextInput name="chargeDescription" label="Description" value={form.description} onChange={(val: any) => setForm((f) => ({ ...f, description: String(val ?? "") }))} />
        <Grid gap={"12px"} columns={["1fr 1fr 64px"]}>
          <TextInput name="chargeCost" label="Provider charges us" prefix="$" placeholder="0.00" value={form.cost} onChange={(val: any) => setForm((f) => ({ ...f, cost: String(val ?? "") }))} />
          <TextInput name="chargePrice" label="Client pays" prefix="$" placeholder={form.cost || "0.00"} value={form.price} onChange={(val: any) => setForm((f) => ({ ...f, price: String(val ?? "") }))} />
          <TextInput name="chargeQty" label="Qty" value={form.quantity} onChange={(val: any) => setForm((f) => ({ ...f, quantity: String(val ?? "") }))} />
        </Grid>
        <Flex sx={{ gap: "12px", padding: "12px 14px", background: "#F7F7F5", borderRadius: "6px", marginBottom: "16px" }}>
          <Figure label="Client invoice" value={money(price * qty)} />
          <Figure label="Provider PO" value={form.applyToPO && selectedPO ? money(cost * qty) : "-"} />
          <Figure label="We keep" value={money((price - (form.applyToPO && selectedPO ? cost : 0)) * qty)} strong />
        </Flex>
        <Box sx={{ marginBottom: "16px" }}>
          {live.length > 0 ? (
            <>
              <OptionRow
                checked={form.applyToPO}
                onChange={(v) => setForm((f) => ({ ...f, applyToPO: v }))}
                title={`Add the provider cost to PO ${gmo(selectedPO ?? live[0])}`}
                detail={`${(selectedPO ?? live[0])?.provider?.name ?? "Provider"} is billed ${money(cost * qty)} on the PO. Untick if the provider is not charging us.`}
              />
              {form.applyToPO && live.length > 1 && (
                <Box sx={{ marginTop: "10px" }}>
                  <SelectInput
                    name="chargePo"
                    label="Which PO"
                    value={form.purchaseOrderId}
                    onChange={(val: any) => setForm((f) => ({ ...f, purchaseOrderId: String(val ?? "") }))}
                    options={live.map((po: any) => ({ value: po.id, label: `${gmo(po)} · ${po?.provider?.name ?? "provider"}` }))}
                  />
                </Box>
              )}
            </>
          ) : (
            <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginTop="0px" marginBottom="0px">No live PO on this job: the charge goes on the client invoice only.</P>
          )}
        </Box>
        <TextInput type="textarea" rows={2} name="chargeReason" label="Reason (saved as a project note)" placeholder="e.g. Site not ready on 11/1, crew returned 11/3" value={form.reason} onChange={(val: any) => setForm((f) => ({ ...f, reason: String(val ?? "") }))} />
        {error && <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.danger} marginTop="4px" marginBottom="0px">{error}</P>}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondaryOutline" type="button" onClick={onClose} style={{ marginRight: "10px" }}>
          Cancel
        </Button>
        <BasicButton type="button" onClick={submit} $submitting={busy} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
          {!busy && <CheckMarkIcon fill={"#fff"} />}
          Add charge
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export const ChargeRemoveModal = ({ target, onClose, onDone }: { target: { invoice: any; chargeId: string; name: string } | null; onClose: () => void; onDone: () => void }) => {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setReason("");
    setError(null);
  }, [target?.chargeId]);
  if (!target) return null;

  const submit = async () => {
    if (!reason.trim()) return setError("Give a reason: it is saved on the project.");
    setBusy(true);
    setError(null);
    const res: any = await removeCharge(target.invoice.id, target.chargeId, reason.trim());
    setBusy(false);
    if (res?.error) return setError(res.error);
    toast.add(`${target.name} removed.`, "var(--theme-ui-colors-success)");
    if (res?.keptOnBilledPurchaseOrders?.length) toast.add(`Left on billed ${res.keptOnBilledPurchaseOrders.join(", ")}: adjust the provider's bill.`, "var(--theme-ui-colors-danger)");
    onClose();
    onDone();
  };

  return (
    <Modal isOpen={!!target} toggle={onClose} centered size={"sm"} closeOnBackdropClick={false}>
      <ModalHeader toggle={onClose}>
        <ModalIcon>
          <InvoicesIcon fill={"#000"} />
        </ModalIcon>
        <ModalTitleWrapper>
          <ModalTitle>Remove {target.name}</ModalTitle>
          <ModalSubtitle>Removed from the client invoice and its PO line. Left on any PO the provider has already billed.</ModalSubtitle>
        </ModalTitleWrapper>
      </ModalHeader>
      <ModalBody>
        <TextInput type="textarea" rows={2} name="removeChargeReason" label="Reason (saved as a project note)" value={reason} onChange={(val: any) => setReason(String(val ?? ""))} />
        {error && <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.danger} marginTop="8px" marginBottom="0px">{error}</P>}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondaryOutline" type="button" onClick={onClose} style={{ marginRight: "10px" }}>
          Cancel
        </Button>
        <BasicButton type="button" onClick={submit} $submitting={busy} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
          Remove charge
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};
